import { useCallback, useEffect, useRef, useState } from "react";
import { loadMessages } from "../lib/messages";
import { messageToEntry } from "../lib/forecast/hydrate.js";
import { entriesLoaded } from "../lib/forecast/reducer.js";
import { readRestoreSignals, hasPendingRestore } from "../lib/forecast/restore.js";
import { runStore } from "../store/runStore.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { bootForecasts } from "../commands/bootForecasts.js";
import {
  loadSharedRerunRuns,
  reattachBranch as cmdReattachBranch,
  reattachToForecast as cmdReattachToForecast,
  resumeSharedReruns,
} from "../commands/resumeForecasts.js";
import { installOpenForecastDetail } from "../commands/loadForecastDetail.js";
import { installForecastPersister } from "../commands/forecastPersister.js";
import { readSharedRerunHandles } from "../lib/forecast/storageKeys.js";

// Command deps (store singleton + real services) — stable, built once at module load.
const cmdDeps = makeDefaultDeps();
const dispatch = runStore.dispatch;

/**
 * Owns the entire one-time boot sequence (EPIC #157, phase P6): the mount effect that runs
 * `commands/bootForecasts.ts` (load → restore decision → resume sweep), the `bootPhase` state
 * machine, the 20s hang safety-timeout, and the debounced persister — replacing
 * `useForecastPersistence` + `useForecastResume` and the `bootPhase`/`hydrationDone`
 * two-flag cross-hook effect chain they raced on. Thin binding — composition/decision logic
 * lives in the commands; this hook only packs React handles into deps and owns the effects'
 * lifecycle.
 *
 * `selectForecastRef`/`resumeTourRef` are forward refs assigned AFTER this hook runs, by the
 * hooks that own the functions: `useForecastSelectionActions` assigns `selectForecastRef.current`
 * during its render (`selectForecast` needs `reattachToForecast` — this hook's return value)
 * and `useOnboardingWiring` assigns `resumeTourRef.current` in a layout effect. This hook reads
 * `.current` only from inside its mount effect, which runs after the full render (including
 * those later assignments) has committed.
 *
 * Retired refs vs. the pre-P6 subsystem (see `commands/resumeForecasts.ts`'s module doc for
 * the resume-side half of this): `hydrationOkRef` is GONE — the load-success/failure signal
 * now travels as a plain boolean return value through the ordered `bootForecasts` call, not a
 * ref two separate effects needed to communicate through. `loadMessagesLatestRef` is GONE as
 * an exposed ref — `claimLoadSlot`/`isCurrentLoadSlot` below replace the raw ref App.jsx's
 * compare-batch hydration used to reach into directly.
 */
export function useBootForecasts({
  me,
  authEnabled,
  authLoading,
  forecasts,
  pinned,
  setPinned,
  autoZoomRef,
  setForecastingPhase,
  setPendingPreprocess,
  setLastObs,
  forecastLiveProgressEnabled,
  setCompareMode,
  debugUI,
  selectForecastRef,
  resumeTourRef,
}) {
  const [bootPhase, setBootPhase] = useState("loading"); // 'loading' | 'restoring' | 'ready' (drives the render gate)
  const forecastsLoaded = bootPhase !== "loading"; // derived, not stored — replaces the old `hydrationDone` flag

  // Read restore signals ONCE, synchronously, before first paint (mirrors the old
  // App.jsx-level restoreSignalsRef) — pendingRestore drives the render gate immediately; the
  // same snapshot is handed to bootForecasts's restore decision below.
  const restoreSignalsRef = useRef(null);
  if (restoreSignalsRef.current === null) restoreSignalsRef.current = readRestoreSignals();
  const pendingRestore = hasPendingRestore(restoreSignalsRef.current);

  // Still consumed OUTSIDE this hook: Lane E1's compare-hydration effect in App.jsx branches
  // success vs. failed-load via `loadedRef.current`.
  const loadedRef = useRef(false);
  const forecastLiveProgressEnabledRef = useRef(forecastLiveProgressEnabled);
  forecastLiveProgressEnabledRef.current = forecastLiveProgressEnabled;

  // Single-flight "latest message load" claim (was the raw `loadMessagesLatestRef`, reached
  // into directly by App.jsx's compare-batch hydration). Hidden behind two functions instead —
  // the ref itself never leaves this module.
  const loadSlotRef = useRef(null);
  const claimLoadSlot = useCallback(() => {
    const token = Symbol("loadSlot");
    loadSlotRef.current = token;
    return token;
  }, []);
  const isCurrentLoadSlot = useCallback((token) => loadSlotRef.current === token, []);

  // Single source for loading a forecast's persisted message thread into its store
  // conversation — used by every chat-load entry point (resume sweep, restored-active,
  // log-click, compare hydration). See useForecastPersistence's old doc (now retired) for the
  // forecastId-vs-conversationId keying rationale; unchanged here.
  const loadConversationThread = useCallback(
    async (forecastId, convId, { token, claim = true } = {}) => {
      if (!forecastId || !convId) return;
      const tok = token ?? forecastId;
      if (claim) loadSlotRef.current = tok;
      const rows = await loadMessages(forecastId);
      if (loadSlotRef.current !== tok) return; // a newer load/navigation claimed the slot
      if (rows.length > 0)
        dispatch(
          entriesLoaded(
            convId,
            rows.map((r) => messageToEntry(r)),
          ),
        );
    },
    [],
  );

  const mkResumeDeps = () => ({
    ...cmdDeps,
    ui: {
      setForecastingPhase,
      setPendingPreprocess,
      setLastObs,
      forecastLiveProgressEnabled: forecastLiveProgressEnabledRef.current,
      setCompareMode,
      debugUI,
    },
    callbacks: { loadConversationThread },
    refs: { autoZoomRef },
  });

  // Public reattach entry point (App's selectForecast + the sweep, via bootForecasts).
  // []-deps (stable identity) — mkResumeDeps() reads current handles at call time.
  const reattachToForecast = useCallback(
    (pending, cancelledRef, opts = {}) =>
      cmdReattachToForecast(mkResumeDeps(), pending, cancelledRef, opts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  // The current deps builder, for the branch reattach the effect below binds at call time.
  const mkResumeDepsRef = useRef(mkResumeDeps);
  mkResumeDepsRef.current = mkResumeDeps;

  // A reload in the middle of updates this tab started on forecasts shared with it: the boot
  // sweep cannot reattach those runs (it covers only the viewer's own forecasts, and the shared
  // list loads after it). Load each forecast's whole tree into the shared lane first
  // (loadSharedRerunRuns: one can be past the lane's first page, and its branches on later pages
  // than it), then reattach what is still running (resumeSharedReruns).
  useEffect(() => {
    if (!forecastsLoaded || !readSharedRerunHandles().length) return undefined;
    const cancelled = { current: false };
    const env = {
      store: runStore,
      reattach: reattachToForecast,
      reattachBranch: (handle) => cmdReattachBranch(mkResumeDepsRef.current(), handle),
    };
    loadSharedRerunRuns(cmdDeps, {
      cancelled: () => cancelled.current,
      // `{...page, ...prev}`: an optimistic pin made since the fetch started still wins.
      onPinned: (pagePinned) => setPinned((prev) => ({ ...pagePinned, ...prev })),
    })
      // Only on a whole tree: deciding on a partial one could drop the handle while a branch
      // that did not load is still running. A failed search leaves it for the next reload.
      .then((outcome) => {
        if (outcome === "found" && !cancelled.current) resumeSharedReruns(env, cancelled);
      })
      .catch((e) => console.warn("shared rerun recovery failed", e));
    return () => {
      cancelled.current = true;
    };
  }, [forecastsLoaded, reattachToForecast, setPinned]);

  // Live mirror of the persister's non-store inputs (forecasts/pinned aren't in the run-store —
  // pinned is React state, and `forecasts` is a watchlist-merged derivation of it). Assigned
  // during render so the debounced subscription callback (fired well after any given render)
  // always reads the latest values — same idiom as the codebase's `runStateRef.current = runState`
  // render-time mirror.
  const persistInputsRef = useRef({ forecasts, pinned });
  persistInputsRef.current = { forecasts, pinned };
  const getPersistInputs = useCallback(() => persistInputsRef.current, []);

  // Flips true once `bootForecasts` reports a genuine authed, successful load — set from
  // `onLoaded` below. Real React STATE (not a ref): the persister-install effect keys on it so
  // installation is deferred to the render AFTER `setPinned`'s merge has committed, not fired
  // synchronously inside the async boot call (which runs before `setPinned`, and well before
  // any resulting re-render). Installing against the pre-merge `pinned`/`forecasts` would prime
  // the persister's baseline missing any watchlisted-but-outside-the-display-cap rows only
  // `pinned` (not the store) knows about — those would then look "new" on the very next
  // store-driven persist pass and get spuriously re-POSTed (Copilot review, PR #3413).
  const [loadSucceeded, setLoadSucceeded] = useState(false);

  const bootFiredRef = useRef(false);
  useEffect(() => {
    if (bootFiredRef.current) return;
    if (authLoading) return;
    if (authEnabled && !me) return; // waiting for a sign-in (this tab, or a visibility refetch after signing in elsewhere)
    bootFiredRef.current = true;
    const cancelledRef = { current: false };
    const base = mkResumeDeps();
    const deps = {
      ...base,
      refs: { ...base.refs, selectForecastRef, resumeTourRef },
      ui: { ...base.ui, setPinned, setBootPhase },
      callbacks: {
        ...base.callbacks,
        // Fires once the load phase settles — BEFORE the restore decision/resume sweep run.
        // `loaded` (NOT `ok`) drives loadedRef: `ok` is true in legacy/no-auth mode too (nothing
        // failed — there was nothing to fetch), but loadedRef must stay false there — it answers
        // "did a real fetch populate the store," which Lane E1's compare-hydration effect in
        // App.jsx branches on. `loaded` alone also happens to be the exact persister-install
        // condition (`ok && authEnabled` collapses to `loaded`, since `loaded = authEnabled &&
        // ok`) — but that decision is deferred to the effect below, not made here.
        onLoaded: (_ok, loaded) => {
          loadedRef.current = loaded;
          if (loaded) setLoadSucceeded(true);
        },
      },
    };
    bootForecasts(
      deps,
      { authEnabled, restoreSignals: restoreSignalsRef.current },
      cancelledRef,
    ).catch((e) => {
      console.warn("useBootForecasts: boot sequence failed", e);
    });
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, authEnabled, me?.sub]);

  // Installs the debounced persister once `loadSucceeded` flips — i.e. on the render AFTER
  // `setPinned`'s merge committed, so `getPersistInputs()` (which reads the render-time
  // `persistInputsRef` mirror above) primes its baseline against the POST-merge forecasts/pinned,
  // never the stale pre-merge snapshot. `persisterInstalledRef` guards against a StrictMode
  // double-invoke re-installing (same idiom as this file's other fire-once refs).
  const persisterInstalledRef = useRef(false);
  useEffect(() => {
    if (!loadSucceeded || persisterInstalledRef.current) return;
    persisterInstalledRef.current = true;
    const uninstall = installForecastPersister(cmdDeps, getPersistInputs);
    return uninstall;
  }, [loadSucceeded, getPersistInputs]);

  // Reads each lean forecast's detail by id as it opens. Installed at mount, not after the owned
  // list loads: the shared list loads on its own, and a shared forecast opened while the owned
  // list failed still needs its detail. A remount reinstalls it; reading a run twice only
  // re-fills nothing.
  useEffect(() => installOpenForecastDetail(cmdDeps), []);

  // Safety-timeout backstop: if bootPhase never reaches 'ready' within 20s (e.g. a network
  // hang before the load settles), force it to 'ready' so the neutral shell does not hang
  // forever. bootForecasts still runs to completion (and applies the correct restore/resume
  // state) whenever it eventually resolves — this only unblocks the render gate.
  useEffect(() => {
    if (bootPhase === "ready") return;
    const t = setTimeout(() => setBootPhase("ready"), 20000); // last-resort hang backstop
    return () => clearTimeout(t);
  }, [bootPhase]);

  return {
    bootPhase,
    forecastsLoaded,
    pendingRestore,
    loadConversationThread,
    reattachToForecast,
    loadedRef,
    claimLoadSlot,
    isCurrentLoadSlot,
  };
}
